import { gql } from '@apollo/client';
import { getDatoCmsClient, isCmsEnabled } from './datoCmsClient';

/**
 * CMS Layout Service - Two-Phase Query Architecture
 *
 * Phase 1: GET_PRODUCT_STRUCTURE - Lightweight query for initial load
 *   - Fetches product metadata and tab structure (slug, title only)
 *   - No nested content - keeps query complexity low
 *
 * Phase 2: GET_TAB_CONTENT - On-demand query for tab content
 *   - Fetches full nested content for a single tab
 *   - Called lazily when user selects a tab
 *
 */
/**
 * Lightweight query to fetch product structure (tabs metadata only)
 * Used for initial load - no nested content
 */
export const GET_PRODUCT_STRUCTURE = gql`
  query GetProductStructure($productId: IntType!) {
    product(
      filter: { productId: { eq: $productId }, isAvailable: { eq: true } }
    ) {
      id
      productId
      slug
      title
      goLiveSource
      goLiveSourceRules
      _modelApiKey
      tabs {
        slug
        title
        isActive
        _modelApiKey
      }
      models {
        id
        modelId
        modelName
        slug
        _modelApiKey
        tabs {
          slug
          title
          isActive
          _modelApiKey
        }
      }
    }
  }
`;

// ============================================================================
// PHASE 2: TAB CONTENT FRAGMENTS (for on-demand loading)
// ============================================================================

/**
 * Fragment for KpiGrid with tiles
 */
const KPI_GRID_FRAGMENT = `
  ... on KpiGridRecord {
    id
    slug
    config
    _modelApiKey
    tiles {
      id
      _modelApiKey
      config
      title
      secondTitle
      subtitle
    }
  }
`;

/**
 * Reusable fields for CustomReactComponent
 */
const CUSTOM_REACT_COMPONENT_FIELDS = `
  id
  slug
  reactComponentSlug
  availabilityRules
  _modelApiKey
`;

/**
 * Fragment for chart blocks inside Card
 */
const CHART_BLOCKS_FRAGMENT = `
  ... on LineChartRecord {
    id
    slug
    config
    _modelApiKey
  }
  ... on BarChartRecord {
    id
    slug
    config
    _modelApiKey
  }
  ... on ListV2Record {
    id
    slug
    config
    _modelApiKey
  }
  ... on GaugeChartRecord {
    id
    slug
    config
    _modelApiKey
  }
  ... on HorizontalProgressBarListRecord {
    id
    slug
    config
    _modelApiKey
  }
  ... on PieChartRecord {
    id
    slug
    config
    _modelApiKey
  }
  ... on CustomReactComponentRecord {
      ${CUSTOM_REACT_COMPONENT_FIELDS}
  }  
`;

/**
 * Grid base fields (reusable)
 */
const GRID_BASE_FIELDS = `
  id
  slug
  gap
  margin
  padding
  columnsMobile
  columnsTablet
  columnsDesktop
  backgroundColor {
    hex
  }
  renderOrder  
  _modelApiKey
`;

/**
 * Card fragment - contains KpiGrid in content (no nested Grid to reduce complexity)
 */
const CARD_FRAGMENT = `
  id
  slug
  heading
  nested
  config
  _modelApiKey
  headerContent {
    id
    _modelApiKey
    config
  }
  blocks {
    ${CHART_BLOCKS_FRAGMENT}
  }
  content {
    ${KPI_GRID_FRAGMENT}
  }
`;

/**
 * Switcher fragment - contains Grid with Cards in contentSlug
 */
const SWITCHER_FRAGMENT = `
  id
  slug
  size
  defaultIndex
  _modelApiKey
  headerContent {
    ... on CustomReactComponentRecord {
      ${CUSTOM_REACT_COMPONENT_FIELDS}
    }
  }
  items {
    id
    label
    value
    disabled
    availabilityRules
    _modelApiKey
    contentSlug {
      ${GRID_BASE_FIELDS}
      blocks {
        ... on CardRecord {
          ${CARD_FRAGMENT}
        }
        ... on CustomReactComponentRecord {
          ${CUSTOM_REACT_COMPONENT_FIELDS}
        }
      }
    }
  }
`;

/**
 * Nested Grid fragment - used inside Grid's content (1 level of nesting)
 */
const NESTED_GRID_FRAGMENT = `
  ${GRID_BASE_FIELDS}
  blocks {
    ... on CardRecord {
      ${CARD_FRAGMENT}
    }
    ... on CustomReactComponentRecord {
      ${CUSTOM_REACT_COMPONENT_FIELDS}
    }
  }
`;

// ============================================================================
// PRODUCT-SPECIFIC FRAGMENTS (no Switcher, matches legacy query structure)
// ============================================================================

/**
 * Card fragment for products - includes nested Grid in content
 * Matches the legacy query structure where Card content can be KpiGrid or Grid
 * (old query had Grid nesting inside Card for products like APP)
 */
const CARD_FRAGMENT_PRODUCT = `
  id
  slug
  heading
  nested
  config
  _modelApiKey
  headerContent {
    id
    _modelApiKey
    config
  }
  blocks {
    ${CHART_BLOCKS_FRAGMENT}
  }
  content {
    ${KPI_GRID_FRAGMENT}
    ... on GridRecord {
      ${GRID_BASE_FIELDS}
      content {
        ... on GridRecord {
          ${GRID_BASE_FIELDS}
          blocks {
            ... on CardRecord {
              ${CARD_FRAGMENT}
            }
          }
        }
      }
      blocks {
        ... on CardRecord {
          ${CARD_FRAGMENT}
        }
      }
    }
  }
`;

/**
 * Top-level Grid content for product Tabs (no Switcher)
 * Uses CARD_FRAGMENT_PRODUCT which includes Grid nesting in Card content
 */
const GRID_CONTENT_FRAGMENT_PRODUCT = `
  ${GRID_BASE_FIELDS}
  blocks {
    ... on CardRecord {
      ${CARD_FRAGMENT_PRODUCT}
    }
    ... on CustomReactComponentRecord {
      ${CUSTOM_REACT_COMPONENT_FIELDS}
    }
  }
  content {
    ... on GridRecord {
      ${NESTED_GRID_FRAGMENT}
    }
    ... on SwitcherRecord {
      ${SWITCHER_FRAGMENT}
    }  
  }
`;

/**
 * Tab content fragment for products (no Switcher, with Grid nesting in Card)
 */
const TAB_CONTENT_FRAGMENT_PRODUCT = `
  slug
  title
  isActive
  _modelApiKey
  content {
    ... on GridRecord {
      ${GRID_CONTENT_FRAGMENT_PRODUCT}
    }
  }
`;

// ============================================================================
// MODEL-SPECIFIC FRAGMENTS (includes Switcher)
// ============================================================================

/**
 * Top-level Grid content for model Tabs (includes Switcher)
 */
const GRID_CONTENT_FRAGMENT = `
  ${GRID_BASE_FIELDS}
  blocks {
    ... on CardRecord {
      ${CARD_FRAGMENT}
    }
    ... on CustomReactComponentRecord {
      ${CUSTOM_REACT_COMPONENT_FIELDS}
    }
  }
  content {
    ... on GridRecord {
      ${NESTED_GRID_FRAGMENT}
    }
    ... on SwitcherRecord {
      ${SWITCHER_FRAGMENT}
    }
  }
`;

/**
 * Full Tab content fragment with Switcher (used for model-based products)
 */
const TAB_CONTENT_FRAGMENT = `
  slug
  title
  isActive
  _modelApiKey
  content {
    ... on GridRecord {
      ${GRID_CONTENT_FRAGMENT}
    }
  }
`;

// ============================================================================
// PHASE 2: TAB CONTENT QUERIES (Optimized - Direct Collection Queries)
// ============================================================================

/**
 * Query to fetch a specific tab by slug for direct product tabs (no Switcher)
 * Uses allTabs collection with server-side filtering for efficiency
 * Lower query complexity - suitable for products like APP
 */
export const GET_TAB_BY_SLUG = gql`
  query GetTabBySlug($slug: String!) {
    allTabs(filter: { slug: { eq: $slug } }, first: 1) {
      ${TAB_CONTENT_FRAGMENT_PRODUCT}
    }
  }
`;

/**
 * Query to fetch a specific tab by slug for model-based products (with Switcher)
 * Uses allTabs collection with server-side filtering for efficiency
 */
export const GET_TAB_BY_SLUG_WITH_SWITCHER = gql`
  query GetTabBySlugWithSwitcher($slug: String!) {
    allTabs(filter: { slug: { eq: $slug } }, first: 1) {
      ${TAB_CONTENT_FRAGMENT}
    }
  }
`;

/**
 * Query to fetch a specific model by modelId (direct collection query)
 * Uses allModels collection with server-side filtering for efficiency
 * Returns the model with all its tabs (tabs is a linked field, can't filter)
 */
export const GET_MODEL_BY_ID = gql`
  query GetModelById($modelId: IntType!) {
    allProductModels(filter: { modelId: { eq: $modelId } }, first: 1) {
      id
      modelId
      modelName
      slug
      _modelApiKey
      tabs {
        ${TAB_CONTENT_FRAGMENT}
      }
    }
  }
`;

/**
 * Query to fetch product tabs content (scoped by productId)
 * Used for direct product tabs where tab slugs may not be globally unique
 * Uses product-specific fragment (no Switcher)
 */
export const GET_TAB_CONTENT = gql`
  query GetTabContent($productId: IntType!) {
    product(filter: { productId: { eq: $productId }, isAvailable: { eq: true } }) {
      id
      productId
      tabs {
        ${TAB_CONTENT_FRAGMENT_PRODUCT}
      }
    }
  }
`;

/**
 * @deprecated Use GET_MODEL_BY_ID instead
 * Legacy query - kept for backward compatibility
 */
export const GET_MODEL_TAB_CONTENT = gql`
  query GetModelTabContent($productId: IntType!) {
    product(filter: { productId: { eq: $productId }, isAvailable: { eq: true } }) {
      id
      productId
      models {
        id
        modelId
        modelName
        tabs {
          ${TAB_CONTENT_FRAGMENT}
        }
      }
    }
  }
`;

// ============================================================================
// LEGACY: FULL PRODUCT LAYOUT QUERY (kept for backward compatibility)
// ============================================================================

/**
 * Full Tab fragment for models (with Switcher - legacy)
 */
const TAB_FRAGMENT_FULL = `
  slug
  title
  isActive
  _modelApiKey
  content {
    ... on GridRecord {
      ${GRID_CONTENT_FRAGMENT}
    }
  }
`;

/**
 * Tab fragment for direct product tabs (no Switcher - legacy)
 */
const TAB_FRAGMENT_PRODUCT = `
  slug
  title
  isActive
  _modelApiKey
  content {
    ... on GridRecord {
      ${GRID_CONTENT_FRAGMENT_PRODUCT}
    }
  }
`;

/**
 * Legacy full product layout query
 * @deprecated Use GET_PRODUCT_STRUCTURE + GET_TAB_CONTENT instead
 * Product tabs use product-specific fragment (no Switcher)
 * Model tabs use full fragment (with Switcher)
 */
export const GET_PRODUCT_LAYOUT = gql`
  query GetProductLayout($productId: IntType!) {
    product(filter: { productId: { eq: $productId }, isAvailable: { eq: true } }) {
      id
      productId
      slug
      title
      _modelApiKey
      tabs {
        ${TAB_FRAGMENT_PRODUCT}
      }
      models {
        id
        modelId
        modelName
        slug
        _modelApiKey
        tabs {
          ${TAB_FRAGMENT_FULL}
        }
      }
    }
  }
`;

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Fetch product structure (lightweight - tabs metadata only)
 * @param {number} productId - The product ID to fetch structure for
 * @returns {Promise<Object|null>} The structure data or null if not found/error
 */
export const fetchProductStructure = async (productId) => {
  if (!isCmsEnabled()) {
    console.log('[CMS] CMS integration is disabled');
    return null;
  }

  if (!productId) {
    console.warn('[CMS] No productId provided');
    return null;
  }

  const client = getDatoCmsClient();
  if (!client) {
    console.error('[CMS] DatoCMS client not available');
    return null;
  }

  try {
    const { data, errors } = await client.query({
      query: GET_PRODUCT_STRUCTURE,
      variables: { productId: Number.parseInt(productId, 10) },
      fetchPolicy: 'cache-first',
    });

    if (errors && errors.length > 0) {
      console.error('[CMS] GraphQL errors:', errors);
      return null;
    }

    if (!data || !data.product) {
      console.log(`[CMS] No layout found for productId: ${productId}`);
      return null;
    }

    // Filter tabs to only include active ones (isActive === true)
    const filterActiveTabs = (tabs) =>
      tabs?.filter((tab) => tab.isActive === true) || [];

    const filteredProduct = {
      ...data.product,
      tabs: filterActiveTabs(data.product.tabs),
      models: data.product.models?.map((model) => ({
        ...model,
        tabs: filterActiveTabs(model.tabs),
      })),
    };

    return {
      product: filteredProduct,
    };
  } catch (error) {
    console.error('[CMS] Error fetching product structure:', error);
    return null;
  }
};

/**
 * Fetch tab data for a model-based product
 * @private
 */
const fetchModelTabData = async (client, modelId, tabSlug) => {
  const { data, errors } = await client.query({
    query: GET_MODEL_BY_ID,
    variables: { modelId: Number.parseInt(modelId, 10) },
    fetchPolicy: 'cache-first',
  });

  if (errors?.length > 0) {
    console.error('[CMS] GraphQL errors:', errors);
    return null;
  }

  const model = data?.allProductModels?.[0];
  if (!model) {
    console.log(`[CMS] No model found for modelId: ${modelId}`);
    return null;
  }

  return model.tabs?.find((t) => t.slug === tabSlug) ?? null;
};

/**
 * Fetch tab data for a direct product tab (scoped by productId)
 * @private
 */
const fetchProductTabData = async (client, productId, tabSlug) => {
  const { data, errors } = await client.query({
    query: GET_TAB_CONTENT,
    variables: { productId: Number.parseInt(productId, 10) },
    fetchPolicy: 'cache-first',
  });

  if (errors?.length > 0) {
    console.error('[CMS] GraphQL errors:', errors);
    return null;
  }

  if (!data?.product) {
    console.log(`[CMS] No product found for productId: ${productId}`);
    return null;
  }

  return data.product.tabs?.find((t) => t.slug === tabSlug) ?? null;
};

/**
 * Fetch tab content on-demand
 *
 * Strategy:
 * - For model-based products: Use GET_MODEL_BY_ID (server-side filter by modelId)
 *   then filter tabs client-side by slug
 * - For direct product tabs: Use GET_TAB_CONTENT (scoped by productId)
 *   then filter tabs client-side by slug to ensure correct product's tab
 *   (tab slugs like "overview", "user-adoption" are not globally unique)
 *
 * @param {Object} params - Parameters
 * @param {number} params.productId - The product ID (required for direct product tabs)
 * @param {number} [params.modelId] - The model ID (for model-based products)
 * @param {string} params.tabSlug - The tab slug to fetch content for
 * @returns {Promise<Object|null>} The tab content or null if not found/error
 */
export const fetchTabContent = async ({ productId, modelId, tabSlug }) => {
  if (!isCmsEnabled()) {
    console.log('[CMS] CMS integration is disabled');
    return null;
  }

  if (!tabSlug) {
    console.warn('[CMS] Missing tabSlug');
    return null;
  }

  const client = getDatoCmsClient();
  if (!client) {
    console.error('[CMS] DatoCMS client not available');
    return null;
  }

  try {
    let tabData = null;

    if (modelId) {
      tabData = await fetchModelTabData(client, modelId, tabSlug);
    } else if (productId) {
      tabData = await fetchProductTabData(client, productId, tabSlug);
    } else {
      console.warn('[CMS] Missing both productId and modelId');
      return null;
    }

    if (!tabData) {
      console.log(`[CMS] No tab found for slug: ${tabSlug}`);
      return null;
    }

    return tabData.content || [];
  } catch (error) {
    console.error('[CMS] Error fetching tab content:', error);
    return null;
  }
};

/**
 * Fetch product layout from DatoCMS (legacy - full content)
 * @deprecated Use fetchProductStructure + fetchTabContent instead
 * @param {number} productId - The product ID to fetch layout for
 * @returns {Promise<Object|null>} The layout data or null if not found/error
 */
export const fetchProductLayout = async (productId) => {
  if (!isCmsEnabled()) {
    console.log('[CMS] CMS integration is disabled');
    return null;
  }

  if (!productId) {
    console.warn('[CMS] No productId provided');
    return null;
  }

  const client = getDatoCmsClient();
  if (!client) {
    console.error('[CMS] DatoCMS client not available');
    return null;
  }

  try {
    const { data, errors } = await client.query({
      query: GET_PRODUCT_LAYOUT,
      variables: { productId: Number.parseInt(productId, 10) },
      fetchPolicy: 'cache-first',
    });

    if (errors?.length > 0) {
      console.error('[CMS] GraphQL errors:', errors);
      return null;
    }

    if (!data?.product) {
      console.log(`[CMS] No layout found for productId: ${productId}`);
      return null;
    }

    return {
      product: data.product,
    };
  } catch (error) {
    console.error('[CMS] Error fetching product layout:', error);
    return null;
  }
};

/**
 * Check if a product has CMS layout configured
 * Uses lightweight structure query
 * @param {number} productId - The product ID to check
 * @returns {Promise<boolean>}
 */
export const hasProductLayout = async (productId) => {
  const layout = await fetchProductStructure(productId);
  if (!layout?.product) {
    return false;
  }

  // Check for direct tabs on product
  if (layout.product.tabs?.length > 0) {
    return true;
  }

  // Check for models with tabs
  if (layout.product.models?.some((model) => model.tabs?.length > 0)) {
    return true;
  }

  return false;
};
